/* @flow */

import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import MediaQuery from 'react-responsive';

import CalendarIcon from '@uniiverse/library/assets/icons/calendar.svg';
import MarkerIcon from '@uniiverse/library/assets/icons/marker.svg';
import MoneyIcon from '@uniiverse/library/assets/icons/money.svg';
import Button from '@uniiverse/library/components/button/index';

import { displayDate, displayTime, guessZone, stripTimezone } from '../../../lib/display-date';

import style from './index-module.css';

type Props = {
  data: {
    event: Object
  },
  locale: string,
  intl: IntlShape,
  pastEvent: boolean,
  hideButton: ?boolean
};

declare var $u: Object;
const MOBILE_BREAK_POINT: number = 751;
const ACTIVE_RATE_STATE: string = 'ACTIVE';
const FULL_TIME_SLOT_STATE: string = 'FULL';

const EventImage: Function = (props: Props): React$Element<*> => {
  const { data: { event }} = props;

  return <div>
    <a href={event.url} className="unii-skip"><img src={event.imageUrl} className={style.image} /></a>
  </div>;
};

const EventTitle: Function = (props: Props): React$Element<*> => {
  const { data: { event }} = props;

  return <div className={style.title}>
    <a href={event.url} className="unii-skip">{event.title}</a>
  </div>;
};

const EventDate: Function = (props: Props): ?React$Element<*> => {
  const { data: { event }, intl } = props;

  if (event.hiddenDate) {
    return null;
  }

  const totalAvailableTimeSlotCount = event.timeSlots.totalCount;

  // Try to filter available events
  let timeSlots = event.timeSlots.nodes.filter(t => t.state !== FULL_TIME_SLOT_STATE);

  if (timeSlots.length === 0) {
    // In case pagination prevents us from getting any, let's show the first one anyway
    timeSlots = event.timeSlots.nodes;
  }

  let start = null;
  let end = null;
  let tz = null;

  if (event.virtual) {
    start = new Date(1000 * timeSlots[0].startStamp);
    end = new Date(1000 * timeSlots[0].endStamp);
    tz = guessZone(start);
  }
  else {
    start = new Date(stripTimezone(timeSlots[0].startAt));
    end = new Date(stripTimezone(timeSlots[0].endAt));
  }

  const date = displayDate(intl, start, end);
  const time = displayTime(intl, start, end);

  let formattedDate = `${date} ${time} ${tz || ''}`;

  if (totalAvailableTimeSlotCount > 1) {
    formattedDate = <FormattedMessage
      id="profile.more-time-slots"
      defaultMessage={'{formattedDate} + {moreTimeSlotCount, number} more'}
      values={{
        moreTimeSlotCount: totalAvailableTimeSlotCount - 1,
        formattedDate
      }}
    />;
  }

  return <div className={style.meta}>
    <CalendarIcon />
    {formattedDate}
  </div>;
};

const EventLocation: Function = (props: Props): ?React$Element<*> => {
  const { data: { event }} = props;
  let locationName = null;

  if (event.virtual) {
    locationName = <FormattedMessage id="profile.virtualEvent" defaultMessage="Virtual event" />;
  }
  else {
    locationName = event.venueName || event.address;
  }

  if (!locationName) {
    return null;
  }

  return (
    <div className={style.meta}>
      <MarkerIcon />
      {locationName}
    </div>
  );
};

const EventPrice: Function = (props: Props): ?React$Element<*> => {
  const { data: { event }, pastEvent } = props;
  const availablePrices = event.rates.nodes.filter((r) => (r.state === ACTIVE_RATE_STATE)).map((r) => (r.displayPrice));
  const sortedPrice = availablePrices.sort((a, b) => (a - b));
  const lowestPrice = sortedPrice[0];
  const highestPrice = sortedPrice[sortedPrice.length - 1];
  const hidePrice = pastEvent || availablePrices.length === 0;

  if (hidePrice) {
    return null;
  }

  let formattedLowestPrice;
  let formattedHighestPrice;
  let separator;

  if (lowestPrice === 0) {
    formattedLowestPrice = <FormattedMessage id="profile.free-event" defaultMessage="Free" />;
  }
  else {
    formattedLowestPrice = <FormattedNumber currency={event.transactionCurrency} value={lowestPrice} style="currency" />;
  }

  if (highestPrice !== lowestPrice) {
    formattedHighestPrice = <FormattedNumber currency={event.transactionCurrency} value={highestPrice} style="currency" />;
    separator = '-';
  }

  return <div className={style.meta}>
    <MoneyIcon />
    {formattedLowestPrice} {separator} {formattedHighestPrice}
  </div>;
};

const BuyTicketsButton: Function = (props: Props): ?React$Element<*> => {
  const { data: { event }, locale, hideButton } = props;

  if (hideButton) {
    return null;
  }

  if (event.soldOut) {
    return <div className={style.getTickets}>
      <Button type="outline" size="default" disabled={true}>
        <FormattedMessage id="profile.event-sold-out" defaultMessage="Sold out" />
      </Button>
    </div>;
  }

  const openJuno: Function = (): void => {
    $u.open(event.id, { locale });
  };

  return <div className={style.getTickets}>
    <Button type="outline" size="default" onClick={openJuno}>
      <FormattedMessage id="profile.get-tickets" defaultMessage="Get tickets" />
    </Button>
  </div>;
};

const MobileEvent: Function = (props: Props): React$Element<*> => {
  return <div className={style.container}>
    <div className={style.info}>
      <EventImage {...props} />
      <EventTitle {...props} />
    </div>
    <div>
      <div>
        <EventDate {...props} />
        <EventLocation {...props} />
        <EventPrice {...props} />
      </div>
    </div>
    <BuyTicketsButton {...props} />
  </div>;
};

const DesktopEvent: Function = (props: Props): React$Element<*> => {
  return <div className={style.container}>
    <div className={style.info}>
      <EventImage {...props} />
      <div>
        <EventTitle {...props} />
        <div>
          <EventDate {...props} />
          <EventLocation {...props} />
          <EventPrice {...props} />
        </div>
      </div>
    </div>
    <BuyTicketsButton {...props} />
  </div>;
};

const Event: Function = (props: Props): React$Element<*> => (
  <div>
    <MediaQuery maxWidth={MOBILE_BREAK_POINT}>
      <MobileEvent {...props} />
    </MediaQuery>
    <MediaQuery minWidth={MOBILE_BREAK_POINT + 1}>
      <DesktopEvent {...props} />
    </MediaQuery>
  </div>
);

export default Event;



// WEBPACK FOOTER //
// ./src/components/profile/event/index.js